import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { supabase } from "./supabase";

async function throwIfResNotOk(res: Response) {
  if (res.status === 401) {
    // Try to refresh the session - use refreshSession to get new tokens
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (!session || error) {
      // Session truly expired - redirect to login
      window.location.href = '/login';
      throw new Error('SESSION_EXPIRED');
    }
    // If we have a fresh session, the request should be retried with new token
    throw new Error('RETRY_WITH_NEW_TOKEN');
  }

  if (!res.ok) {
    let errorMessage;
    try {
      const errorData = await res.json();
      errorMessage = errorData.message || errorData.error || res.statusText;
      if (errorData.errors) {
        errorMessage += `: ${JSON.stringify(errorData.errors)}`;
      }
    } catch (e) {
      // If it's not JSON, get text content
      errorMessage = await res.text() || res.statusText;
    }
    throw new Error(`${res.status}: ${errorMessage}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  retry: boolean = true
): Promise<Response> {
  // Get the current session
  const { data: { session } } = await supabase.auth.getSession();
  
  
  // Build headers
  const headers: HeadersInit = {};
  
  // Handle different data types
  let body: any;
  if (data instanceof FormData) {
    // For FormData, don't set Content-Type - browser sets it automatically with boundary
    body = data;
  } else if (data) {
    // For other data types, use JSON
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(data);
  }
  
  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  
  const res = await fetch(url, {
    method,
    headers,
    body,
    credentials: "include",
  });

  // Handle 401s with retry
  if (res.status === 401 && retry) {
    // Try to refresh the session to get new tokens
    const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
    
    if (newSession && !error) {
      // Retry with fresh token
      headers["Authorization"] = `Bearer ${newSession.access_token}`;
      const retryRes = await fetch(url, {
        method,
        headers,
        body, // Use the same body we prepared earlier (FormData or JSON string)
        credentials: "include",
      });
      
      // Don't retry again to avoid infinite loop
      if (retryRes.status === 401) {
        window.location.href = '/login';
        throw new Error('SESSION_EXPIRED');
      }
      
      await throwIfResNotOk(retryRes);
      return retryRes;
    } else {
      // Can't refresh - redirect to login
      window.location.href = '/login';
      throw new Error('SESSION_EXPIRED');
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Get the current session - refresh if needed
    let { data: { session } } = await supabase.auth.getSession();
    
    // If no session, try to refresh
    if (!session) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData.session;
    }
    
    
    // Build headers
    const headers: HeadersInit = {};
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }
    
    const res = await fetch(queryKey[0] as string, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,   // 10 minutes
      retry: 1,                  // Retry once on failure
      refetchOnWindowFocus: false, // Prevent excessive refetching
      refetchOnReconnect: true,    // Refetch on network reconnect
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
    },
  },
});

// Make queryClient accessible globally for logout
if (typeof window !== 'undefined') {
  (window as any).queryClient = queryClient;
}
